'use client';

export type ClientErrorReport = {
  message: string;
  stack?: string | null;
  source?: string | null;
  lineno?: number | null;
  colno?: number | null;
  url?: string | null;
  userAgent?: string | null;
  route?: string | null;
  timestamp: string;
  type: 'error' | 'unhandledrejection' | 'react';
};

const REPORT_ENDPOINT = '/api/client-error';
const THROTTLE_MS = 30_000;
const MAX_PER_SESSION = 5;

// Known noisy errors we don't want to report
const IGNORED_ERROR_PATTERNS = [
  /Cannot read propert(?:y|ies) of undefined \(reading ['"]products['"]\)/i,
  // Instagram in-app browser (WKWebView) injects scripts that access
  // window.webkit.messageHandlers; this fails when the expected handler is
  // not registered and is not caused by our application code.
  /window\.webkit\.messageHandlers/i,
  // Generic CORS-masked errors from third-party scripts (browser extensions,
  // social in-app browsers, trackers). The browser hides the real source,
  // so these reports are not actionable.
  /^Script error\.?$/i,
];

let lastReportAt = 0;
let sessionReportCount = 0;

function shouldThrottle(): boolean {
  const now = Date.now();
  if (sessionReportCount >= MAX_PER_SESSION) return true;
  if (now - lastReportAt < THROTTLE_MS) return true;
  return false;
}

function shouldIgnore(message: string): boolean {
  return IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

type ReportOptions = Omit<ClientErrorReport, 'message' | 'stack' | 'timestamp' | 'type'> & {
  type?: ClientErrorReport['type'];
};

export async function reportClientError(
  error: Error | string | unknown,
  options: ReportOptions = {}
): Promise<void> {
  if (typeof window === 'undefined') return;

  if (shouldThrottle()) {
    console.warn('[client-error-reporter] throttled, skipping report');
    return;
  }

  const err = error instanceof Error ? error : new Error(String(error || 'Unknown client error'));

  if (shouldIgnore(err.message)) {
    console.warn('[client-error-reporter] ignored known error:', err.message);
    return;
  }

  const report: ClientErrorReport = {
    ...options,
    message: err.message || 'Unknown client error',
    stack: err.stack || null,
    url: window.location?.href || null,
    userAgent: navigator?.userAgent || null,
    route: window.location?.pathname || null,
    timestamp: new Date().toISOString(),
    type: options.type || 'error',
  };

  lastReportAt = Date.now();
  sessionReportCount += 1;

  try {
    await fetch(REPORT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report),
    });
  } catch (e) {
    // Silent fail: we don't want error reporting to crash the app
    console.error('[client-error-reporter] failed to send report', e);
  }
}
